'use client';

import { useEffect, useRef, useState } from 'react';

import { usePathname, useRouter } from 'next/navigation';

import { ChevronDown, ChevronRight, Lock, MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
} from '@kit/ui/drawer';

import { useNativePresentation } from '~/(tenant)/_components/native/_lib/use-native-presentation';
import { useNativeTokens } from '~/(tenant)/_components/native/_lib/use-native-tokens';
import {
  ACTIVE_LOCATION_CHANGED_EVENT,
  type ActiveLocationChangedDetail,
  activeLocationCookieKey,
  dispatchActiveLocationChanged,
} from '~/lib/tenant/active-location-cookie';

import type { LocationData } from './sections/location-section';

// LocationData with the access mode tag carried from the booking-types RPC.
// Code-only venues use a different tap target (deep-link into /book where
// the existing code-prompt flow lives) instead of the cookie write.
type LocationWithAccess = LocationData & { accessMode?: string };

interface TopBarLocationSwitcherProps {
  /** Tenant UUID — namespaces the cookie so multi-tenant users keep distinct
   *  preferences. */
  accountId: string;
  /** All active locations for the tenant. Component self-hides when there
   *  are fewer than two (nothing to switch to). */
  locations: LocationWithAccess[];
}

const cookieKey = activeLocationCookieKey;

// Routes whose server loaders call getActiveLocation() and therefore render
// different data per venue. Only these justify blocking a first-time visitor
// with the forced pick. Everything else (a deep-linked payment page, bookings,
// profile, pass) renders the same regardless of venue, so the chip stays
// available in the top bar but the page loads untouched.
const VENUE_AWARE_ROUTES = [
  '/book',
  '/packages',
  '/memberships',
  '/coaches',
  '/class-categories',
];

function isVenueAwareRoute(pathname: string): boolean {
  if (pathname === '/') return true;
  return VENUE_AWARE_ROUTES.some(
    (route) => pathname === route || pathname.startsWith(`${route}/`),
  );
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.split('=')[1] ?? '') : null;
}

function writeCookie(name: string, value: string) {
  // Gate Secure on https so the cookie still sets in local dev (http://localhost).
  const secure = window.location.protocol === 'https:' ? ';Secure' : '';
  document.cookie = `${name}=${encodeURIComponent(value)};path=/;max-age=31536000;SameSite=Lax${secure}`;
}

/**
 * Compact venue switcher rendered in the native top bar (right side).
 * Tap to open a draggable native-feeling bottom drawer (powered by vaul via
 * @kit/ui/drawer). The sheet is drag-to-close on every route, including the
 * auto-opened first-pick prompt. Picking a venue writes the
 * `book:last-location:<tenantId>` cookie and triggers a router refresh so
 * location-aware server components re-render scoped to the new choice.
 *
 * Code-only venues deep-link into `/book?location=<slug>` instead — the
 * existing code-prompt flow there handles unlock.
 *
 * Single-location tenants get nothing rendered — there's no useful action.
 */
export function TopBarLocationSwitcher({
  accountId,
  locations,
}: TopBarLocationSwitcherProps) {
  const router = useRouter();
  const pathname = usePathname();
  const t = useNativeTokens();
  const { t: tr } = useTranslation('bookings');
  const presentation = useNativePresentation();

  const [activeSlug, setActiveSlug] = useState<string | null>(null);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  // Tracks whether the cookie was *missing* on mount (first-time visitor).
  // When true, the drawer auto-opens with "pick your venue" copy. It is
  // still dismissible (drag handle, scrim tap, back gesture) on every route:
  // a sheet that refuses to close reads as a frozen app. Dismissing without
  // a pick leaves the page on its no-cookie fallback (the alphabetic default
  // shown in the chip) and the user can reopen the chip whenever they like.
  const [needsInitialPick, setNeedsInitialPick] = useState(false);
  // Once the user has swiped the first-pick sheet away we don't re-open it
  // on the next venue-aware navigation — asking once per session is a
  // prompt, asking on every tab tap is a nag. A reload asks again.
  const dismissedFirstPickRef = useRef(false);

  // Read cookie after mount so the chip reflects the same choice the SSR
  // resolver used. If no cookie is set and there are multiple venues, prompt
  // the user to pick one explicitly — but only on a route whose content
  // actually depends on the venue. Re-evaluated on navigation so a visitor
  // who lands on /payment and later taps Book still gets asked there.
  useEffect(() => {
    const saved = readCookie(cookieKey(accountId));
    setActiveSlug(saved);
    if (
      !saved &&
      locations.length >= 2 &&
      isVenueAwareRoute(pathname) &&
      !dismissedFirstPickRef.current
    ) {
      setNeedsInitialPick(true);
      setIsPickerOpen(true);
    }
  }, [accountId, locations.length, pathname]);

  // Listen for in-tab location changes (e.g. the booking-form's inline
  // location picker on /book) and update the chip without waiting for a
  // navigation. Filtered by tenantId so a future multi-tenant scenario
  // can't bleed between accounts.
  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<ActiveLocationChangedDetail>).detail;
      if (!detail || detail.tenantId !== accountId) return;
      setActiveSlug(detail.slug);
      // Every writer of this event has just settled the venue (the /book
      // inline picker, handlePick below, or AdoptVenue on a payment page),
      // so the sheet has nothing left to ask — including the forced
      // first-pick, if it opened before the page adopted its own venue.
      setNeedsInitialPick(false);
      setIsPickerOpen(false);
    };
    window.addEventListener(ACTIVE_LOCATION_CHANGED_EVENT, handler);
    return () => {
      window.removeEventListener(ACTIVE_LOCATION_CHANGED_EVENT, handler);
    };
  }, [accountId]);

  if (locations.length < 2) return null;

  const sorted = [...locations].sort((a, b) => a.name.localeCompare(b.name));
  const active = sorted.find((l) => l.slug === activeSlug) ?? sorted[0] ?? null;
  if (!active) return null;

  const handlePick = (loc: LocationWithAccess) => {
    setIsPickerOpen(false);
    setNeedsInitialPick(false);

    // Code-only venues route through /book where the existing code-prompt
    // flow handles unlock. Don't write the cookie yet — that happens after
    // the user enters a valid code.
    if (loc.accessMode === 'code_only') {
      router.push(`/book?location=${encodeURIComponent(loc.slug)}`);
      return;
    }

    // On the first-pick path, write the cookie even when the slug matches
    // the alphabetic-default display so the page exits "no cookie" state.
    // Returning users picking the same venue: no-op refresh.
    const isSameAsDisplay = loc.slug === active.slug;
    if (isSameAsDisplay && !needsInitialPick) return;

    writeCookie(cookieKey(accountId), loc.slug);
    try {
      window.localStorage.setItem(cookieKey(accountId), loc.slug);
    } catch {
      // Storage quota / disabled — cookie is the source of truth anyway.
    }
    setActiveSlug(loc.slug);
    // Notify any other in-tab listeners (e.g. /book's inline picker if the
    // user later navigates back to it without a hard refresh) so their
    // local state stays consistent with the cookie.
    dispatchActiveLocationChanged({ tenantId: accountId, slug: loc.slug });
    // Re-runs server components on the current route with the new cookie so
    // location-aware sections re-fetch their scoped data.
    router.refresh();
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setIsPickerOpen(true)}
        className="flex h-8 items-center gap-1 rounded-full px-2.5 transition-opacity active:opacity-70"
        style={{
          color: t.foreground,
          background: `${t.foreground}10`,
          maxWidth: 160,
        }}
        aria-label={tr('venueSwitcher.changeLocation', {
          defaultValue: 'Change location',
        })}
      >
        <MapPin
          size={13}
          strokeWidth={1.5}
          style={{ opacity: 0.75, flexShrink: 0 }}
        />
        <span
          style={{
            fontFamily: t.fontDisplay,
            fontSize: 12.5,
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
        >
          {active.name}
        </span>
        <ChevronDown
          size={13}
          strokeWidth={1.5}
          style={{ opacity: 0.6, flexShrink: 0 }}
        />
      </button>

      <Drawer
        open={isPickerOpen}
        onOpenChange={(next) => {
          // Always honour a user-driven close (drag, scrim tap, back). If the
          // first-pick prompt is being swiped away without a choice, remember
          // that so the next navigation doesn't immediately re-open it.
          if (!next && needsInitialPick) {
            dismissedFirstPickRef.current = true;
            setNeedsInitialPick(false);
          }
          setIsPickerOpen(next);
        }}
      >
        <DrawerContent
          data-native-surface
          className={presentation.className}
          // Dim + blur the page behind the sheet. Without a scrim the page
          // underneath reads as live, so people tap the back arrow, nothing
          // happens (the drawer is modal), and they assume the app froze.
          blockOutside
          overlayClassName="bg-black/45 backdrop-blur-[2px] dark:bg-black/60"
          // Override the default `bg-background` from @kit/ui/drawer so the
          // sheet picks up the tenant's native color tokens instead of the
          // global Tailwind theme. The drawer's built-in drag handle stays.
          style={{
            ...presentation.style,
            background: t.background,
            color: t.foreground,
          }}
        >
          <div style={{ padding: '8px 22px 28px' }}>
            <DrawerTitle asChild>
              <div style={{ marginBottom: 6 }}>
                <div
                  style={{
                    fontFamily: t.fontMono,
                    fontSize: 9,
                    letterSpacing: '0.22em',
                    textTransform: 'uppercase',
                    color: t.muted,
                    marginBottom: 4,
                  }}
                >
                  {needsInitialPick
                    ? tr('venueSwitcher.welcomeEyebrow', {
                        defaultValue: 'Welcome',
                      })
                    : tr('venueSwitcher.eyebrow', { defaultValue: 'Venue' })}
                </div>
                <h2
                  style={{
                    fontFamily: t.fontDisplay,
                    fontSize: 22,
                    fontWeight: 500,
                    margin: 0,
                    letterSpacing: '-0.01em',
                    color: t.foreground,
                  }}
                >
                  {needsInitialPick
                    ? tr('venueSwitcher.pickYourVenue', {
                        defaultValue: 'Pick your venue to get started',
                      })
                    : tr('venueSwitcher.chooseVenue', {
                        defaultValue: 'Choose a venue',
                      })}
                </h2>
              </div>
            </DrawerTitle>
            <DrawerDescription className="sr-only">
              {tr('venueSwitcher.description', {
                defaultValue: 'Switch the active venue for your bookings.',
              })}
            </DrawerDescription>

            <div
              style={{
                marginTop: 18,
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
              }}
            >
              {sorted.map((loc) => {
                // Only a venue the user actually saved is "current". Before
                // the first pick the chip shows an alphabetic guess, and
                // labelling that as current would suggest the choice is made.
                const isActive =
                  activeSlug !== null && loc.slug === active.slug;
                const isCodeOnly = loc.accessMode === 'code_only';
                return (
                  <button
                    key={loc.id}
                    data-native-selection-row
                    type="button"
                    aria-pressed={isActive}
                    onClick={() => handlePick(loc)}
                    className="transition-opacity active:opacity-70"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 14,
                      width: '100%',
                      padding: '16px 16px',
                      background: t.surface,
                      border: `1.5px solid ${isActive ? t.accent : t.border}`,
                      borderRadius: t.radius,
                      cursor: 'pointer',
                      textAlign: 'start',
                      color: t.foreground,
                    }}
                  >
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 36,
                        height: 36,
                        borderRadius: t.radius,
                        background: isActive ? t.accent : t.line,
                        color: isActive ? t.background : t.foreground,
                        flexShrink: 0,
                      }}
                    >
                      {isCodeOnly ? (
                        <Lock size={17} strokeWidth={1.5} />
                      ) : (
                        <MapPin size={17} strokeWidth={1.5} />
                      )}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontFamily: t.fontDisplay,
                          fontSize: 17,
                          fontWeight: isActive ? 600 : 500,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {loc.name}
                      </div>
                      {loc.city && (
                        <div
                          style={{
                            fontFamily: t.fontMono,
                            fontSize: 9,
                            letterSpacing: '0.18em',
                            textTransform: 'uppercase',
                            color: t.muted,
                            marginTop: 4,
                          }}
                        >
                          {loc.city}
                        </div>
                      )}
                    </div>
                    {isActive ? (
                      <span
                        style={{
                          fontFamily: t.fontMono,
                          fontSize: 9,
                          letterSpacing: '0.18em',
                          textTransform: 'uppercase',
                          color: t.accent,
                          flexShrink: 0,
                        }}
                      >
                        {tr('venueSwitcher.current', {
                          defaultValue: 'Current',
                        })}
                      </span>
                    ) : (
                      <ChevronRight
                        size={18}
                        strokeWidth={1.5}
                        className="rtl:rotate-180"
                        style={{ color: t.muted, flexShrink: 0 }}
                      />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
}
